import { FC, ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { ListAssetCategories } from '@/entities/assets/components/ListAssetCategories';
import { DropdownButtonContactType } from '@/entities/contact/components/DropdownButtonContactType';
import { PidTypeListToSelect } from '@/entities/personalIdentifiers/components/PidTypeListToSelect';
import { DropdownButton } from '@/shared/components/DropdownButton';
import {
  AssetCategory,
  CollectionName,
  ContactType,
  isAssetCategory,
  isContactType,
  isPersonalIdentifierType,
  PersonalIdentifierType,
} from '@/shared/lib/sj-orm';
import { Button } from '@/shared/uikitV2/Button';

import { BtnClose } from '../../components/BtnClose';
import { SelectItem } from '../../components/SelectItem';
import { SelectOptions } from '../../components/SelectItem/types';
import { useModalsStore } from '../../modals.store';
import { SelectEntitiesModalProps } from '../../types';

import { CreateAsset } from './CreateAsset';
import { CreateContact } from './CreateContact';
import { CreateDocument } from './CreateDocument';
import { CreatePersonalId } from './CreatePersonalId';
import {
  Actions,
  Body,
  Container,
  Footer,
  Header,
  StyledDrawer,
  StyledTitle,
  TabsStyled,
} from './styled';
import { AllowedSelectEntityName } from './types';

export const SelectEntitiesModal: FC<SelectEntitiesModalProps> = ({
  value,
  multiple,
  createEntityTypes,
  createEntityCategories,
  title,
  colNames,
  category,
  excludeIds,
  onSelect,
  onClose,
  filter,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectEntities' });
  const { t: tsc } = useTranslation('shared/collections', { keyPrefix: 'names' });
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<
    AssetCategory | PersonalIdentifierType | ContactType | null
  >(null);
  const [tab, setTab] = useState<AllowedSelectEntityName>(colNames[0]);
  const modals = useModalsStore();
  const formId = useId();

  const tabs = useMemo(() => colNames.map((key) => ({ key, label: tsc(key) })), [colNames]);

  const handleClose = () => {
    modals.closeModal();
    onClose?.();
  };

  const handleSelectType = (type: AssetCategory | PersonalIdentifierType | ContactType) => {
    setCreateOpen(true);
    setSelectedType(type);
  };

  const handleSubmit = (data: SelectOptions) => {
    handleClose();
    onSelect(data.id);
  };

  const assetCategory = selectedType && isAssetCategory(selectedType) ? selectedType : undefined;
  const personalIdType =
    selectedType && isPersonalIdentifierType(selectedType) ? selectedType : undefined;
  const contactType = selectedType && isContactType(selectedType) ? selectedType : undefined;

  const CreateComponents: Record<AllowedSelectEntityName, ReactNode> = {
    [CollectionName.ASSETS]: <CreateAsset category={assetCategory} setOpen={setCreateOpen} />,
    [CollectionName.PRIVATE_DOCUMENTS]: (
      <CreateDocument privateCategory={category} setOpen={setCreateOpen} />
    ),
    [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS]: (
      <CreatePersonalId personalIdType={personalIdType} setOpen={setCreateOpen} />
    ),
    [CollectionName.CONTACTS]: <CreateContact type={contactType} setOpen={setCreateOpen} />,
  };

  const hasTabs = colNames.length > 1;
  const createAssetCategories = createEntityCategories?.filter((item) => isAssetCategory(item));

  return (
    <>
      <StyledDrawer width={800} footer={null} closable={false} onClose={handleClose} open>
        <Container>
          <Header className={cn({ hasTabs })}>
            <BtnClose onClick={handleClose} style={{ position: 'absolute', right: 24, top: 24 }} />
            <StyledTitle className="truncate" size={22} bold>
              {title}
            </StyledTitle>
            {hasTabs && (
              <TabsStyled
                defaultValue={tab}
                items={tabs}
                onChange={(tabValue) => setTab(tabValue as AllowedSelectEntityName)}
                centered
              />
            )}
          </Header>
          <Body>
            <SelectItem
              multiple={multiple}
              formId={formId}
              colName={tab}
              values={Array.isArray(value) ? value : value ? [value] : []}
              excludeIds={excludeIds}
              filter={filter}
              onSelect={handleSubmit}
            />
          </Body>
          <Footer>
            <Button type="text" onClick={handleClose}>
              {t('btnCancel')}
            </Button>
            <Actions>
              {tab === CollectionName.ASSETS ? (
                <DropdownButton
                  listPosV="top"
                  btnVariant="default"
                  listStyle={{ maxHeight: 620 }}
                  onSelect={handleSelectType}
                  renderList={(handleSelect, listRef, selectStyles) => (
                    <ListAssetCategories
                      filter={createAssetCategories}
                      style={selectStyles}
                      onSelect={handleSelect}
                      ref={listRef}
                    />
                  )}
                >
                  {t('btnAddNew')}
                </DropdownButton>
              ) : tab === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS ? (
                <DropdownButton
                  listPosV="top"
                  btnVariant="default"
                  listStyle={{ maxHeight: 620 }}
                  onSelect={handleSelectType}
                  renderList={(handleSelect, listRef, selectStyles) => (
                    <PidTypeListToSelect
                      style={selectStyles}
                      onSelect={handleSelect}
                      ref={listRef}
                    />
                  )}
                >
                  {t('btnAddNew')}
                </DropdownButton>
              ) : tab === CollectionName.CONTACTS ? (
                <DropdownButtonContactType
                  onSelect={handleSelectType}
                  text={t('btnAddNew')}
                  filter={(filterType) => Boolean(createEntityTypes?.includes(filterType))}
                />
              ) : (
                <Button
                  type="default"
                  onClick={() => setCreateOpen(true)}
                  style={{ marginLeft: 'auto' }}
                >
                  {t('btnAddNew')}
                </Button>
              )}
              <Button htmlType="submit" form={formId}>
                {t('btnConfirm')}
              </Button>
            </Actions>
          </Footer>
        </Container>
      </StyledDrawer>
      {createOpen && CreateComponents[tab]}
    </>
  );
};
